import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { Prompt, RouteComponentProps } from "react-router-dom";
import CalibrationApi from "../../api/CalibrationApi";
import PlateApi from "../../api/PlateApi";
import PlateTypeApi from "../../api/PlateTypeApi";
import PlateTypeCaptureProfileApi from "../../api/PlateTypeCaptureProfileApi";
import IconImageMask from "../../Icons/PlateTypeEditor/ImageMask";
import IconSync from "../../Icons/PlateTypeEditor/Sync";
import IconDefaultImagingSettings from "../../Icons/TopCommandPanel/DefaultImagingSettings";
import IconExport from "../../Icons/TopCommandPanel/Export";
import IconImport from "../../Icons/TopCommandPanel/Import";
import IconMoreAction from "../../Icons/TopCommandPanel/MoreAction";
import IconPlateTypeCalibration from "../../Icons/TopCommandPanel/PlateTypeCalibration";
import IconBackupToDatabase from "../../Icons/TopCommandPanel/BackupToDatabase";
import IconRestoreFromDatabase from "../../Icons/TopCommandPanel/RestoreFromDatabase";
import RefPlateIcon from "../../Icons/TopCommandPanel/RefPlate";
import {
  ELightPath,
  PlateTypeCaptureProfile,
} from "../../Model/CaptureProfile";
import { EModuleType } from "../../Model/EModuleType";
import { ENotificationType } from "../../Model/ENotificationType";
import { EMaskShape, EMaskShapeExtended } from "../../Model/ImageMask";
import { Module } from "../../Model/Module";
import { Plate, PlateTypeBackupEntry } from "../../Model/Plate";
import {
  DropCalibrationPoint,
  PlateType,
  PlateTypeCalibration,
} from "../../Model/PlateType";
import { getModuleState } from "../../redux/reducers/ModuleStateReducer";
import { getImagerCapabilities } from "../../redux/reducers/ImagerCapabilitiesReducer";
import { getModules } from "../../redux/reducers/ModuleReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import WaypointUtil from "../../Utility/WaypointUtil";
import Wait from "../../Wait";
import MtiButton from "../Buttons/MtiButton";
import SwitchButton from "../Buttons/SwitchButton";
import Instruction from "../Common/Instruction";
import LiveStreamHeaderButton from "../Common/LiveStreamHeaderButton";
import Modal from "../Common/Modal";
import HeaderButton from "../Header/HeaderButton";
import HeaderDropDownButton from "../Header/HeaderDropDownButton";
import ImagerManualControl from "../Imager/ImagerManualControl";
import LayoutContent from "../LayoutContent";
import NavSubItem, { NavSubItemWithPermission } from "../Sidebar/NavSubItem";
import SidebarSubmenu from "../Sidebar/SidebarSubmenu";
import {
  sendNotification,
  showToast,
} from "./../../redux/actions/NotificationAction";
import Breadcrumb from "./../Header/Breadcrumb";
import Header from "./../Header/Header";
import HeaderSelector from "./../Header/HeaderSelector";
import LiveImage from "./../Imager/LiveImage";
import DefaultImagingSettings from "./DefaultImagingSettings";
import { DisplayValue } from "./DefaultImagingSettingViews/DefaultImagingSettingComboBox";
import ImageMaskCanvas from "./ImageMaskViews/ImageMaskCanvas";
import ImageMaskSelection from "./ImageMaskViews/ImageMaskSelection";
import PlateTypeCalibrationView from "./PlateTypeCalibration";
import { EComponentId } from "../../Model/Permission";
import ImagerUtil from "../../Utility/ImagerUtil";
import { EArmType } from "../../Model/Waypoint";
import ImagerApi from "../../api/ImagerApi";
import SetAsReferencePlate from "../../Icons/TopCommandPanel/SetAsReferencePlate";
import { getPlateAddressesOnModule } from "../../redux/reducers/PlateAddressReducer";
import PlateTypeBackupTable from "./PlateTypeBackupTable";
import WarningModalView from "./WarningModalView";
import BackupPlateTypeModalView from "./BackupPlateTypeModalView";
import RestorePlateTypeModalView from "./RestorePlateTypeModalView";
import { CartesianCoordinate } from "../../Model/Coordinate";

const CONFIG = "config";
const CALIBRATION = "calibration";
const IMAGE_MASK = "image-mask";

function getImagerOptions(imagerModules: Module[]): DisplayValue[] {
  return imagerModules.map((module) => {
    return {
      value: module.identifier,
      display: module.name,
    };
  });
}

function getExtendedMaskShape(
  captureProfile: PlateTypeCaptureProfile
): EMaskShapeExtended {
  const imageMask = captureProfile ? captureProfile.imageMask : null;
  if (imageMask == null) {
    return EMaskShapeExtended.Circle;
  }
  switch (imageMask.shape) {
    case EMaskShape.Ellipse: {
      if (imageMask.widthInMicrons === imageMask.heightInMicrons) {
        return EMaskShapeExtended.Circle;
      } else {
        return EMaskShapeExtended.Ellipse;
      }
    }
    case EMaskShape.Rectangle: {
      if (imageMask.widthInMicrons === imageMask.heightInMicrons) {
        return EMaskShapeExtended.Square;
      } else {
        return EMaskShapeExtended.Rectangle;
      }
    }
    case EMaskShape.RoundedRectangle: {
      if (imageMask.widthInMicrons === imageMask.heightInMicrons) {
        return EMaskShapeExtended.RoundedSquare;
      } else {
        return EMaskShapeExtended.RoundedRectangle;
      }
    }
  }
}

type TParams = {
  subMenu: string;
};

function PlateTypeEditor({ match }: RouteComponentProps<TParams>) {
  const dispatch = useDispatch();
  const imagerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.IMAGER)
  );
  const imagerCapabilitiesCollection = useTypedSelector((state) =>
    getImagerCapabilities(state)
  );
  const [selectedImagerIdentifier, setSelectedImagerIdentifier] = useState(
    imagerModules[0].identifier
  );
  const [
    isPlateTypeCaptureProfileUpdated,
    setIsPlateTypeCaptureProfileUpdated,
  ] = useState(false);
  const [isPlateTypeCalibrationUpdated, setIsPlateTypeCalibrationUpdated] =
    useState(false);
  const [plateTypeCalibration, setPlateTypeCalibration] = useState(
    null as PlateTypeCalibration | null
  );
  const [plateTypeCaptureProfiles, setPlateTypeCaptureProfiles] = useState(
    [] as PlateTypeCaptureProfile[]
  );
  const [errorList, setErrorList] = useState([] as string[]);
  const [extendedMaskShape, setExtendedMaskShape] = useState(
    EMaskShapeExtended.Circle
  );
  const [originalExtendedMaskShape, setOriginalExtendedMaskShape] = useState(
    EMaskShapeExtended.Circle
  );
  const [waypointNames, setWaypointNames] = useState(null as string[] | null);
  const [wellLimitEnabled, setWellLimitEnabled] = useState(true);
  const plateOnImager = useTypedSelector(
    (state) => getModuleState(state, selectedImagerIdentifier).imager?.plate,
    (left, right) =>
      left?.barcode === right?.barcode &&
      left?.isReferencePlate === right?.isReferencePlate
  );
  const freerunCapture = useTypedSelector(
    (state) =>
      getModuleState(state, selectedImagerIdentifier)?.imager?.freerunCapture ??
      false
  );

  const [selectedPlateTypeId, setSelectedPlateTypeId] = useState(-1);
  const [plateTypes, setPlateTypes] = useState([] as PlateType[]);
  const body = match.params.subMenu ? match.params.subMenu : CONFIG;
  const contentDisplay = useRef<HTMLDivElement>(null);
  const [showApplyForAllWarning, setShowApplyForAllWarning] = useState(false);
  const [wellLimitWarning, setWellLimitWarning] = useState(false);
  const [plateReference, setPlateReference] = useState(null as Plate | null);
  const fileSelector = useRef<HTMLInputElement>(null);
  const [plateTypeUploadXml, setPlateTypeUploadXml] = useState("");
  const [showWarningUploadXml, setShowWarningUploadXml] = useState(false);
  const [showBackupPlateType, setShowBackupPlateType] = useState(false);
  const [backupPlateTypeNote, setBackupPlateTypeNote] = useState("");
  const [showRestorePlateType, setShowRestorePlateType] = useState(false);
  const [selectedPlateTypeBackupEntry, setSelectedPlateTypeBackupEntry] =
    useState(null as PlateTypeBackupEntry | null);
  const [refPlateDropCalPointString, setRefPlateDropCalPointString] =
    useState("");
  const [refPlateOnProgress, setRefPlateOnProgress] = useState(false);
  const [showSetRefPlateModal, setShowSetRefPlateModal] = useState(false);
  const [oldReferencePlate, setoldReferencePlate] = useState("");
  const [newReferencePlate, setnewReferencePlate] = useState("");
  const [showWarningImgMaskAccess, setShowWarningImgMaskAccess] =
    useState(false);

  let isSelectedPlateTypeEqualReferencePlateType =
    selectedPlateTypeId === plateReference?.plateType.id;

  const plateTypeOnImager = useTypedSelector((state) =>
    getPlateAddressesOnModule(state, selectedImagerIdentifier)
  );

  let selectedPlateTypeExistsInImager =
    selectedPlateTypeId === plateTypeOnImager[0].usedByPlate?.plateType.id;

  const ongoingOldReferencePlate = useTypedSelector((state) =>
    getPlateAddressesOnModule(state, "LoadPort-1")
  );

  useEffect(() => {
    if (plateTypeOnImager[0] !== undefined) {
      setnewReferencePlate(
        () => plateTypeOnImager[0]?.usedByPlate?.experimentName as string
      );
    }
    if (ongoingOldReferencePlate !== undefined) {
      ongoingOldReferencePlate.map((item) => {
        if (item.reservedByPlate?.isReferencePlate === true) {
          setoldReferencePlate(
            () => item.reservedByPlate?.experimentName as string
          );
        }
      });
    }
  }, [newReferencePlate, oldReferencePlate]);

  const suiteState = useTypedSelector(
    (state) => getModuleState(state, "Suite").suite
  );
  const imagerModuleState = useTypedSelector((state) =>
    getModuleState(state, selectedImagerIdentifier)
  );
  const imagerState = imagerModuleState.imager;

  const lightPaths =
    imagerCapabilitiesCollection.byId[selectedImagerIdentifier]
      .lightpathCollection.allIds;

  //DropLocation Adjustment for SLP Calibration
  const visibleObjectiveZoom =
    imagerState?.captureProfile.visible?.objectiveZoom;
  const [needLoadDropPosition, setNeedLoadDropPositions] = useState(false);

  useEffect(() => {
    if (visibleObjectiveZoom !== undefined || visibleObjectiveZoom !== null) {
      setNeedLoadDropPositions(true);
    }
  }, [visibleObjectiveZoom]);

  useEffect(() => {
    if (
      !isPlateTypeCalibrationUpdated &&
      body === CALIBRATION &&
      needLoadDropPosition
    ) {
      onCancel();
    }
  }, [body, isPlateTypeCalibrationUpdated, needLoadDropPosition]);

  useEffect(() => {
    updatePlateTypes(
      selectedImagerIdentifier,
      plateReference?.plateType.id ?? -1
    );
  }, [plateReference]);

  useEffect(() => {
    setWaypointNames(null);
    CalibrationApi.getWaypoint().then((moduleWaypoints) => {
      var waypointNames = WaypointUtil.getWaypointNames(
        moduleWaypoints,
        selectedImagerIdentifier,
        EArmType.ImagingArm
      );
      setWaypointNames(waypointNames);
    });
  }, []);

  useEffect(() => {
    const preventReload = (e: BeforeUnloadEvent) => {
      if (isPlateTypeCalibrationUpdated || isPlateTypeCaptureProfileUpdated) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = "";
      } else {
        delete e["returnValue"];
      }
    };

    window.addEventListener("beforeunload", preventReload);

    return () => {
      window.removeEventListener("beforeunload", preventReload);
    };
  }, [isPlateTypeCalibrationUpdated, isPlateTypeCaptureProfileUpdated]);

  useEffect(() => {
    onCancel();
  }, [body]);

  useEffect(() => {
    PlateApi.getReferencePlate().then((plateRef) => {
      setPlateReference(plateRef);
    });
  }, []);

  const onApply = () => {
    if (isPlateTypeCalibrationUpdated && plateTypeCalibration != null) {
      if (!isWellLimitAllowed()) {
        setWellLimitWarning(true);
      } else if (
        (plateOnImager?.isReferencePlate &&
          plateReference?.plateType.id === selectedPlateType.id &&
          JSON.stringify(plateTypeCalibration.calibrationPoints) !==
            refPlateDropCalPointString) ??
        false
      ) {
        setShowApplyForAllWarning(true);
      } else {
        PlateTypeApi.setPlateTypeCalibration(
          selectedPlateType.id,
          selectedImagerIdentifier,
          plateTypeCalibration,
          false
        ).then(() => {
          dispatch(
            showToast({
              duration: 2000,
              message: "The changes are successfully saved",
              variant: "success",
            })
          );
        });
        setIsPlateTypeCalibrationUpdated(false);
        setNeedLoadDropPositions(false);
      }
    }

    if (isPlateTypeCaptureProfileUpdated) {
      setOriginalExtendedMaskShape(extendedMaskShape);

      for (let i = 0; i < plateTypeCaptureProfiles.length; i++) {
        PlateTypeCaptureProfileApi.updatePlateTypeCaptureProfile(
          plateTypeCaptureProfiles[i]
        );
      }
      setIsPlateTypeCaptureProfileUpdated(false);
    }
  };

  // get extender mask shape after fetch from api
  const initiateMaskShapeAFterFetching = useCallback(
    (plateTypeCaptureProfiles: PlateTypeCaptureProfile[]) => {
      const visiblePlateTypeCaptureProfile = plateTypeCaptureProfiles.filter(
        (p) => p.lightpath === ELightPath.Visible
      )[0];
      const maskShape = getExtendedMaskShape(visiblePlateTypeCaptureProfile);
      setExtendedMaskShape(maskShape);
    },
    []
  );

  const onCancel = () => {
    if (plateTypes.length === 0) {
      return;
    }
    PlateTypeCaptureProfileApi.getPlateTypeCaptureProfiles(
      selectedPlateTypeId,
      selectedImagerIdentifier,
      undefined
    ).then((defaultImagingProfiles) => {
      initiateMaskShapeAFterFetching(defaultImagingProfiles);
      setPlateTypeCaptureProfiles(defaultImagingProfiles);
    });

    PlateTypeApi.getPlateTypeCalibration(
      selectedPlateTypeId,
      selectedImagerIdentifier,
      true
    ).then((plateTypeCalibration) => {
      setPlateTypeCalibration(plateTypeCalibration);
    });

    setIsPlateTypeCalibrationUpdated(false);
    setIsPlateTypeCaptureProfileUpdated(false);
    setExtendedMaskShape(originalExtendedMaskShape);
  };

  const onErrorOccur = (key: string) => {
    let newError: string[] = [...errorList];
    newError.push(key);
    setErrorList(newError);
  };

  const onErrorCleared = (key: string) => {
    let newError: string[] = [...errorList];
    const index = errorList.indexOf(key);
    if (index !== -1) {
      newError.splice(index, 1);
      setErrorList(newError);
    }
  };

  const isWellLimitAllowed = () => {
    const wellLimit = plateTypeCalibration?.wellLimit;

    if (wellLimitEnabled && wellLimit) {
      if (
        wellLimit.min.x > 0 ||
        wellLimit.min.y > 0 ||
        wellLimit.min.z > 0 ||
        wellLimit.max.x < 0 ||
        wellLimit.max.y < 0 ||
        wellLimit.max.z < 0
      ) {
        return false;
      }
    }

    return true;
  };

  const getWellLimitWarning = () => {
    const wellLimit = plateTypeCalibration?.wellLimit;

    let minWarningAxis = "";
    let maxWarningAxis = "";

    if (wellLimit) {
      if (wellLimit.min.x > 0) {
        minWarningAxis += "X";
      }

      if (wellLimit.min.y > 0) {
        minWarningAxis += ", Y";
      }

      if (wellLimit.min.z > 0) {
        minWarningAxis += ", Z";
      }

      if (wellLimit.max.x < 0) {
        maxWarningAxis += "X";
      }

      if (wellLimit.max.y < 0) {
        maxWarningAxis += ", Y";
      }

      if (wellLimit.max.z < 0) {
        maxWarningAxis += ", Z";
      }
    }

    let minWarning = minWarningAxis
      ? `"Well Limits - Min ${minWarningAxis}" must be less than or equal to 0`
      : "";
    let maxWarning = maxWarningAxis
      ? `"Well Limits - Max ${maxWarningAxis}" must be at least 0`
      : "";

    return minWarning + "\n" + maxWarning;
  };

  const onImagerChanged = (imagerIdentifier: string) => {
    setSelectedImagerIdentifier(imagerIdentifier);
    updatePlateTypes(imagerIdentifier, plateReference?.plateType.id ?? -1);
  };

  const updatePlateTypes = (
    imagerIdentifier: string,
    plateReferencePlateTypeId: number
  ) => {
    PlateTypeApi.getPlateTypes().then((plateTypes) => {
      if (plateTypes.length > 0) {
        let id = plateTypes[0].id;
        if (plateOnImager != null) {
          const plateType = plateTypes.find(
            (plateType) => plateType.id === plateOnImager?.plateType.id
          );
          if (plateType != null) {
            id = plateType.id;
          }
        }
        setSelectedPlateTypeId(id);
        setPlateTypes(plateTypes);
        updatePlateTypeCalibration(
          id,
          imagerIdentifier,
          plateReferencePlateTypeId
        );
        updatePlateTypeCaptureProfile(id, imagerIdentifier);
      }
    });
  };

  const updatePlateTypeCalibration = (
    plateTypeId: number,
    imagerIdentifier: string,
    plateReferencePlateTypeId: number
  ) => {
    PlateTypeApi.getPlateTypeCalibration(
      plateTypeId,
      imagerIdentifier,
      true
    ).then((plateTypeCalibration) => {
      setPlateTypeCalibration(plateTypeCalibration);
      if (plateTypeId === plateReferencePlateTypeId) {
        setRefPlateDropCalPointString(
          JSON.stringify(plateTypeCalibration?.calibrationPoints)
        );
      }
    });

    setIsPlateTypeCalibrationUpdated(false);
    setIsPlateTypeCaptureProfileUpdated(false);
  };

  const updatePlateTypeCaptureProfile = (
    plateTypeId: number,
    imagerIdentifier: string
  ) => {
    PlateTypeCaptureProfileApi.getPlateTypeCaptureProfiles(
      plateTypeId,
      imagerIdentifier,
      undefined
    ).then((plateTypeCaptureProfiles) => {
      initiateMaskShapeAFterFetching(plateTypeCaptureProfiles);
      setPlateTypeCaptureProfiles(plateTypeCaptureProfiles);
    });
  };

  const onPlateTypeChanged = (plateTypeIdentifier: number) => {
    setSelectedPlateTypeId(plateTypeIdentifier);
    updatePlateTypeCalibration(
      plateTypeIdentifier,
      selectedImagerIdentifier,
      plateReference?.plateType.id ?? -1
    );
    updatePlateTypeCaptureProfile(
      plateTypeIdentifier,
      selectedImagerIdentifier
    );
  };

  const exportClick = () => {
    PlateTypeApi.exportPlateTypeXml(
      selectedPlateTypeId,
      selectedImagerIdentifier,
      selectedPlateType.plateTypeName
    );
  };
  const iimportClick = () => {
    fileSelector?.current?.click();
  };
  const floatingClick = () => {
    window.open("/plate-type-editor-floating", "floating-plate-type-editor");
  };

  const moreActionsClick = (id: string) => {
    switch (id) {
      case "IMPORT":
        iimportClick();
        break;
      case "EXPORT":
        exportClick();
        break;
      case "BACKUP":
        setShowBackupPlateType(true);
        break;
      case "RECOVER":
        setShowRestorePlateType(true);
        break;
      case "SYNC":
        PlateTypeApi.sync().then(() =>
          PlateTypeApi.getPlateTypes().then((plateTypes) => {
            setPlateTypes(plateTypes);
          })
        );
        break;
      case "FLOATING":
        floatingClick();
        break;
      default:
        break;
    }
  };

  if (waypointNames == null) {
    return <Wait message="Loading waypoint names..." />;
  }

  if (plateTypes.length === 0) {
    return <Wait message="Loading plate types..." />;
  }

  if (plateTypeCalibration == null) {
    return <Wait message="Loading plate type calibration..." />;
  }

  if (plateTypeCaptureProfiles.length === 0) {
    return <Wait message="Loading plate type capture profile..." />;
  }

  if (suiteState == null) {
    throw new Error("Suite state is null");
  }

  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  const imagerManualControlEnabled = !ImagerUtil.getIsOpticControlDisabled(
    imagerState,
    suiteState
  );

  const plateTypeOptions = plateTypes.map((plate: PlateType) => {
    const onClick = () => {
      if (
        plate.id !== plateTypeOnImager[0].usedByPlate?.plateType.id &&
        body === IMAGE_MASK
      ) {
        setShowWarningImgMaskAccess(true);
      }
    };

    return {
      value: plate.id.toString(),
      display: `${plate.plateTypeName} ${
        plate.id === plateReference?.plateType.id ? "(Reference Plate)" : ""
      }`,
      icon:
        plate.id === plateReference?.plateType.id ? (
          <RefPlateIcon />
        ) : undefined,
      canBeClicked:
        plate.id !== plateTypeOnImager[0].usedByPlate?.plateType.id &&
        body === IMAGE_MASK
          ? false
          : true,
      onClick: onClick,
    };
  });

  const selectedPlateType = plateTypes.filter(
    (plateType: PlateType) => plateType.id === selectedPlateTypeId
  )[0];

  /**
   * Always save the plate mask information with Visible lightPath only.
   * To avoid duplicate data and to save space, we do not save it with the other lightpaths.
   * The plate mask information is defined per plate type, not per lightpath.
   * All lightpaths of the same plate type will use the same plate mask.
   */
  const visiblePlateTypeCaptureProfile = plateTypeCaptureProfiles.filter(
    (p) => p.lightpath === ELightPath.Visible
  )[0];

  const onPlateTypeCaptureProfileUpdated = (
    captureProfile: PlateTypeCaptureProfile
  ) => {
    setIsPlateTypeCaptureProfileUpdated(true);
    const otherPlateTypeCaptureProfile = plateTypeCaptureProfiles.filter(
      (cp) => cp.lightpath !== captureProfile.lightpath
    );
    setPlateTypeCaptureProfiles([
      ...otherPlateTypeCaptureProfile,
      {
        ...captureProfile,
      },
    ]);
  };

  const onPlateTypeCalibrationUpdated = (
    plateTypeCalibration: PlateTypeCalibration
  ) => {
    setIsPlateTypeCalibrationUpdated(true);
    setPlateTypeCalibration(plateTypeCalibration);
  };

  const moreActionOptions = [
    {
      value: "IMPORT",
      display: "Import",
      icon: <IconImport />,
      disabled: selectedPlateType.id === (plateReference?.plateType.id ?? -1),
    },
    {
      value: "EXPORT",
      display: "Export",
      icon: <IconExport />,
      disabled: selectedPlateType.id === (plateReference?.plateType.id ?? -1),
    },
    {
      value: "BACKUP",
      display: "Backup",
      icon: <IconBackupToDatabase />,
      disabled: selectedPlateType.id === (plateReference?.plateType.id ?? -1),
    },
    {
      value: "RECOVER",
      display: "Recover",
      icon: <IconRestoreFromDatabase />,
      disabled: selectedPlateType.id === (plateReference?.plateType.id ?? -1),
    },
    {
      value: "SYNC",
      display: "Reload Plate Types",
      icon: <IconSync />,
      disabled: false,
    },
    // {
    //   value: "BACKUP",
    //   display: "Backup To Database",
    //   icon: <IconBackupToDatabase />,
    //   disabled: false,
    // },
    // {
    //   value: "FLOATING",
    //   display: "Floating Plate Type Editor",
    //   icon: <IconPlateTypeCalibration />,
    //   disabled: false,
    // },
  ];

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const reader = new FileReader();
    reader.onload = async (e) => {
      if (e.target != null) {
        const text = e.target.result;
        if (text === "" || typeof text !== "string") {
          dispatch(
            sendNotification(
              ENotificationType.Error,
              "File is empty or corrupted."
            )
          );
        } else {
          PlateTypeApi.checkPlateTypeXml(text).then(() => {
            setPlateTypeUploadXml(text);
            setShowWarningUploadXml(true);
          });
        }
      }
    };
    if (e.target?.files != null && e.target?.files.length > 0) {
      reader.readAsText(e.target.files[0]);
      if (fileSelector.current) {
        fileSelector.current.value = "";
      }
    }
  };

  const lpc = plateTypeCaptureProfiles.filter(
    (cp) => cp.lightpath === ELightPath.Visible
  )[0];

  let isDropPositionCorrect = true;
  if (plateTypeCalibration?.calibrationPoints.length) {
    for (let i = 0; i < plateTypeCalibration.calibrationPoints.length; i++) {
      const point = plateTypeCalibration?.calibrationPoints[i];

      for (const key in point) {
        const position = point[key as keyof DropCalibrationPoint];
        for (const positionKey in position) {
          if (
            position[positionKey as keyof CartesianCoordinate] <
              imagerState?.minPosition[
                positionKey as keyof CartesianCoordinate
              ] ||
            position[positionKey as keyof CartesianCoordinate] >
              imagerState?.maxPosition[positionKey as keyof CartesianCoordinate]
          ) {
            isDropPositionCorrect = false;
            break; // Exit the inner loop if the condition is met
          }
        }
      }

      if (!isDropPositionCorrect) {
        break; // Exit the outer loop if the condition is met
      }
    }
  }

  const onClickYesModal = async () => {
    try {
      await PlateTypeApi.setPlateTypeCalibration( selectedPlateType.id, selectedImagerIdentifier, plateTypeCalibration, true)
      dispatch(
        showToast({
          duration: 2000,
          message: "The changes are successfully saved",
          variant: "success",
        })
      );
      setShowApplyForAllWarning(false);
      setIsPlateTypeCalibrationUpdated(false);
      if (selectedPlateTypeId === plateReference?.plateType.id) {
        setRefPlateDropCalPointString(
          JSON.stringify(plateTypeCalibration.calibrationPoints)
        );
      }
      setNeedLoadDropPositions(false);
    } catch (error) {
      dispatch(
        showToast({
          duration: 2000,
          message: "The changes are failed",
          variant: "error",
        })
      );
    }
    
  }

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          {body === CALIBRATION && (
            <Breadcrumb
              items={[
                {
                  title: "Plate Type Calibration",
                  to: "/plate-type-editor/calibration",
                  disabled: false,
                },
              ]}
            />
          )}
          {body === CONFIG && (
            <Breadcrumb
              items={[
                {
                  title: "Default Imaging Settings",
                  to: "/plate-type-editor/config",
                  disabled: false,
                },
              ]}
            />
          )}
          {body === IMAGE_MASK && (
            <Breadcrumb
              items={[
                {
                  title: "Image Mask",
                  to: "/plate-type-editor/image-mask",
                  disabled: false,
                },
              ]}
            />
          )}
          <HeaderSelector
            value={selectedPlateTypeId.toString()}
            onChange={(value) => onPlateTypeChanged(Number.parseInt(value))}
            displayValues={plateTypeOptions}
            noArrow={false}
          />
          {imagerModules.length > 1 && (
            <HeaderSelector
              value={selectedImagerIdentifier}
              onChange={(identifier) => onImagerChanged(identifier)}
              displayValues={getImagerOptions(imagerModules)}
              noArrow={false}
              disabled={imagerModules.length === 1}
            />
          )}
          {/* <HeaderSelector
            value={selectedLightpath.toString()}
            onChange={(lp) => setSelectedLightpath(Number.parseInt(lp))}
            displayValues={getLightpathOptions(imagerCapabilities)}
            noArrow={false}
          /> */}
          <Header.RightSide>
            <LiveStreamHeaderButton
              freerunCapture={freerunCapture}
              imagerIdentifier={selectedImagerIdentifier}
              disabled={!imagerManualControlEnabled}
            />
            <HeaderButton
              onClick={() => setShowSetRefPlateModal(true)}
              label="Set as Reference Plate"
              content={<SetAsReferencePlate />}
              id={"SET_AS_REFERENCE_PLATE"}
              disabled={
                refPlateOnProgress ||
                isSelectedPlateTypeEqualReferencePlateType ||
                !selectedPlateTypeExistsInImager
              }
            />
            {body === IMAGE_MASK && (
              <HeaderButton
                disabled={false}
                id={"USE_IMAGE_MASK"}
                label={"Image Mask"}
                content={
                  <SwitchButton
                    isOn={
                      visiblePlateTypeCaptureProfile.plateMaskEnabled
                        ? true
                        : false
                    }
                    withLetter={true}
                    onToggle={() => {}}
                  />
                }
                onClick={(id) =>
                  onPlateTypeCaptureProfileUpdated({
                    ...visiblePlateTypeCaptureProfile,
                    plateMaskEnabled:
                      !visiblePlateTypeCaptureProfile.plateMaskEnabled,
                  })
                }
              />
            )}
            {body !== IMAGE_MASK && (
              <HeaderDropDownButton
                id={"MORE_ACTIONS"}
                label={"More Actions"}
                icon={<IconMoreAction />}
                onChange={moreActionsClick}
                iconDisplayValues={moreActionOptions}
              />
            )}
            <MtiButton
              size="large"
              variant="secondary"
              disabled={
                !isPlateTypeCalibrationUpdated &&
                !isPlateTypeCaptureProfileUpdated
              }
              style={{
                margin: "auto 10px",
              }}
              onClick={onCancel}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              disabled={
                (!isPlateTypeCalibrationUpdated &&
                  !isPlateTypeCaptureProfileUpdated) ||
                errorList.length > 0 ||
                !isDropPositionCorrect
              }
              style={{
                margin: "auto 10px",
              }}
              onClick={onApply}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <SidebarSubmenu>
          <NavSubItemWithPermission
            label="Plate Type Calibration"
            key="plateTypeCalibration"
            to="/plate-type-editor/calibration"
            icon={<IconPlateTypeCalibration />}
            componentId={EComponentId.PlateSetupPlateTypeCalibration}
          />
          <NavSubItemWithPermission
            label="Def. Img. Settings"
            key="defaultImagingSettings"
            to="/plate-type-editor/config"
            icon={<IconDefaultImagingSettings />}
            componentId={EComponentId.PlateSetupDefImgSetting}
          />
          <NavSubItemWithPermission
            label="Image Mask"
            key="imageMask"
            to="/plate-type-editor/image-mask"
            icon={<IconImageMask />}
            componentId={EComponentId.PlateSetupImageMask}
            disabled={!imagerManualControlEnabled}
          />
        </SidebarSubmenu>
      </LayoutContent.SubMenu>
      <LayoutContent.Content>
        <div
          ref={contentDisplay}
          style={{
            position: "relative",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <LiveImage imagerIdentifier={selectedImagerIdentifier} />
          {body === IMAGE_MASK && (
            <ImageMaskCanvas
              extendedMaskShape={extendedMaskShape}
              captureProfile={visiblePlateTypeCaptureProfile}
              setCaptureProfile={onPlateTypeCaptureProfileUpdated}
              imagerIdentifier={selectedImagerIdentifier}
              container={contentDisplay}
            />
          )}
        </div>
        <BackupPlateTypeModalView
          showBackupPlateType={showBackupPlateType}
          setShowBackupPlateType={setShowBackupPlateType}
          backupPlateTypeNote={backupPlateTypeNote}
          setBackupPlateTypeNote={setBackupPlateTypeNote}
          selectedPlateTypeId={selectedPlateTypeId}
          selectedImagerIdentifier={selectedImagerIdentifier}
        />
        <RestorePlateTypeModalView
          showRestorePlateType={showRestorePlateType}
          setShowRestorePlateType={setShowRestorePlateType}
          selectedPlateTypeBackupEntry={selectedPlateTypeBackupEntry}
          setSelectedPlateTypeBackupEntry={setSelectedPlateTypeBackupEntry}
          selectedPlateTypeId={selectedPlateTypeId}
          selectedImagerIdentifier={selectedImagerIdentifier}
        />
        <Modal
          show={showSetRefPlateModal}
          onHide={() => {
            setShowSetRefPlateModal(false);
          }}
          hideOnBackdropClick={true}
        >
          <Modal.Header
            title="Set As Reference Plate"
            hideButtonEnabled={false}
          />
          <Modal.Body style={{ textAlign: "left" }}>
            <p style={{ margin: 0 }}>
              Set this plate {selectedPlateType.plateTypeName} as Reference
              Plate?
              <br />
            </p>
            <Instruction
              text={`Please note that ${selectedPlateType.plateTypeName} will replacing ${plateReference?.plateType.plateTypeName} as reference plate.`}
              variant="warning"
              style={{ marginTop: 30, paddingTop: 10, paddingBottom: 10 }}
            />
          </Modal.Body>
          <Modal.Footer
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              marginTop: 30,
            }}
          >
            <MtiButton
              size="large"
              disabled={
                refPlateOnProgress ||
                isSelectedPlateTypeEqualReferencePlateType ||
                !selectedPlateTypeExistsInImager
              }
              onClick={async () => {
                try {
                  await ImagerApi.setCurrentPlateAsReferencePlate(
                    imagerModules[0].identifier
                  );

                  setShowSetRefPlateModal(false);
                  dispatch(
                    sendNotification(
                      ENotificationType.Success,
                      "A reference plate has been setted successfully"
                    )
                  );

                  setRefPlateOnProgress(true);
                  const refPlate = await PlateApi.getReferencePlate();
                  if (refPlate) {
                    if (refPlate.barcode === imagerState?.plate?.barcode) {
                      setRefPlateOnProgress(false);
                    }
                  }
                } catch (err) {
                  console.log("Kembalian error !", err);
                }
              }}
            >
              CONTINUE
            </MtiButton>
            <MtiButton
              size="large"
              style={{ border: "2px solid #f7a246", marginRight: 10 }}
              variant="secondary"
              onClick={() => {
                PlateTypeApi.setPlateTypeCalibration(
                  selectedPlateType.id,
                  selectedImagerIdentifier,
                  plateTypeCalibration,
                  false
                );
                setShowApplyForAllWarning(false);
                setIsPlateTypeCalibrationUpdated(false);
                setShowSetRefPlateModal(false);
              }}
            >
              CANCEL
            </MtiButton>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showApplyForAllWarning}
          hideOnBackdropClick={false}
          onHide={() => {
            setShowApplyForAllWarning(false);
          }}
        >
          <Modal.Header title="Confirmation" hideButtonEnabled={true} />
          <Modal.Body style={{ textAlign: "left" }}>
            <p style={{ margin: 0 }}>
              Reference plate calibration.
              <br />
              <br />
              Do you want to update Reference Points for all other plate types?
            </p>
            <Instruction
              text="This Action will apply the calibration for all other plate types"
              variant="warning"
              style={{ marginTop: 30 }}
            />
          </Modal.Body>
          <Modal.Footer
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              marginTop: 30,
            }}
          >
            <MtiButton
              size="large"
              onClick={onClickYesModal}
            >
              YES
            </MtiButton>
            <MtiButton
              size="large"
              variant="secondary"
              style={{
                marginRight: 10,
              }}
              onClick={() => {
                PlateTypeApi.setPlateTypeCalibration(
                  selectedPlateType.id,
                  selectedImagerIdentifier,
                  plateTypeCalibration,
                  false
                );
                setShowApplyForAllWarning(false);
                setIsPlateTypeCalibrationUpdated(false);
                if (selectedPlateTypeId === plateReference?.plateType.id) {
                  setRefPlateDropCalPointString(
                    JSON.stringify(plateTypeCalibration.calibrationPoints)
                  );
                }
              }}
            >
              NO
            </MtiButton>
          </Modal.Footer>
        </Modal>
        {wellLimitWarning && (
          <Modal
            show={wellLimitWarning}
            hideOnBackdropClick={true}
            onHide={() => setWellLimitWarning(false)}
          >
            <Modal.Header title="Well Limits" hideButtonEnabled={true} />
            <Modal.Body style={{ textAlign: "left" }}>
              <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {getWellLimitWarning()}
              </p>
            </Modal.Body>
            <Modal.Footer
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                marginTop: 30,
              }}
            >
              <MtiButton
                size="large"
                onClick={() => {
                  setWellLimitWarning(false);
                }}
              >
                OK
              </MtiButton>
            </Modal.Footer>
          </Modal>
        )}
        <Modal
          show={showWarningUploadXml}
          hideOnBackdropClick={false}
          onHide={() => setShowWarningUploadXml(false)}
        >
          <Modal.Header
            title="Plate Definition Update"
            hideButtonEnabled={false}
          />
          <Modal.Body style={{ textAlign: "left" }}>
            <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
              Do you want to apply default configuration from selected Plate
              Definition file?
            </p>
            <Instruction
              text="This Action will apply the the configuraton from the Plate Definition File"
              variant="warning"
              style={{ marginTop: 30 }}
            />
          </Modal.Body>
          <Modal.Footer
            style={{
              display: "flex",
              flexDirection: "row-reverse",
              marginTop: 30,
            }}
          >
            <MtiButton
              size="large"
              variant="secondary"
              onClick={() => {
                setShowWarningUploadXml(false);
              }}
              style={{ marginLeft: 10 }}
            >
              Cancel
            </MtiButton>
            <MtiButton
              size="large"
              onClick={() => {
                PlateTypeApi.importPlateTypeXml(
                  selectedPlateTypeId,
                  selectedImagerIdentifier,
                  plateTypeUploadXml
                ).then(() => {
                  PlateTypeApi.getPlateTypeCalibration(
                    selectedPlateTypeId,
                    selectedImagerIdentifier,
                    true
                  ).then((plateTypeCalibration) => {
                    setPlateTypeCalibration(plateTypeCalibration);
                    updatePlateTypeCaptureProfile(selectedPlateTypeId, selectedImagerIdentifier)
                  });
                  setShowWarningUploadXml(false);
                });
              }}
            >
              Yes
            </MtiButton>
          </Modal.Footer>
        </Modal>
        <Modal
          show={showWarningImgMaskAccess}
          hideOnBackdropClick={true}
          onHide={() => setShowWarningImgMaskAccess(false)}
          style={{
            width: 686,
            minHeight: 422,
          }}
        >
          <Modal.Body>
            <WarningModalView
              message="You are not allow to change Plate Type"
              subMessage="Image Mask feature is only allowed for selected plate on Imager"
              onClickOk={() => setShowWarningImgMaskAccess(false)}
            />
          </Modal.Body>
          <Modal.Footer
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <MtiButton
              variant="primary"
              size="large"
              style={{
                width: 157,
              }}
              onClick={() => setShowWarningImgMaskAccess(false)}
            >
              OK
            </MtiButton>
          </Modal.Footer>
        </Modal>
      </LayoutContent.Content>
      <LayoutContent.CommandPanel>
        <div
          style={{
            backgroundColor: "white",
            height: "100%",
            overflow: "auto",
          }}
        >
          {body === CALIBRATION && (
            <PlateTypeCalibrationView
              imagerIdentifier={selectedImagerIdentifier}
              plateType={selectedPlateType}
              plateTypeCalibration={plateTypeCalibration}
              setPlateTypeCalibration={onPlateTypeCalibrationUpdated}
              waypointNames={waypointNames}
              wellLimitEnabled={wellLimitEnabled}
              setWellLimitEnabled={setWellLimitEnabled}
            />
          )}

          {body === CONFIG && (
            <DefaultImagingSettings
              lightPaths={lightPaths}
              imagerIdentifier={selectedImagerIdentifier}
              captureProfiles={plateTypeCaptureProfiles}
              setCaptureProfile={onPlateTypeCaptureProfileUpdated}
              onErrorOccur={onErrorOccur}
              onErrorCleared={onErrorCleared}
              wellLimitEnabled={wellLimitEnabled}
              plateType={selectedPlateType}
              isSelectedPlateTypeInImager={selectedPlateTypeExistsInImager}
              style={{
                overflow: "auto",
              }}
            />
          )}
          {body === IMAGE_MASK && (
            <>
              <ImageMaskSelection
                extendedMaskShape={extendedMaskShape}
                captureProfile={visiblePlateTypeCaptureProfile}
                setExtendedMaskShape={setExtendedMaskShape}
                setCaptureProfile={onPlateTypeCaptureProfileUpdated}
              />
              <ImagerManualControl
                wellLimitEnabled={wellLimitEnabled}
                imagerIdentifier={selectedImagerIdentifier}
                style={{
                  overflow: "none",
                }}
              />
            </>
          )}
          <Prompt
            when={
              isPlateTypeCalibrationUpdated || isPlateTypeCaptureProfileUpdated
            }
            message={(location) =>
              `Changes are not saved, are you sure you want to leave Plate Setup - ${
                body === CONFIG
                  ? "Default Imaging Settings"
                  : body === IMAGE_MASK
                  ? "Image Mask"
                  : "Plate Type Calibration"
              }?`
            }
          />
          <input
            ref={fileSelector}
            type="file"
            onChange={(e) => uploadFile(e)}
            style={{ display: "none" }}
            accept=".xml"
          />
        </div>
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}

function plateTypeEditorPropsAreEqual(
  prev: RouteComponentProps<TParams>,
  next: RouteComponentProps<TParams>
) {
  return prev.match.params.subMenu === next.match.params.subMenu;
}

export default React.memo(PlateTypeEditor, plateTypeEditorPropsAreEqual);

PlateTypeEditor.whyDidYouRender = true;
